import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import Elevation, { ElevationProps } from 'components/Elevation';
import HorizontalDivider from 'components/HorizontalDivider';
import { zIndex } from 'styles';

import OverlayPanelContainer from './Animated.OverlayPanelContainer';

const ELEVATION_CONFIG = visible => ({
  elevationIndex: visible ? 4 as const : 0 as const,
  dark: true
});

const ELEVATION_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'flex-start',
  maxHeight: '100%',
  minHeight: '100%',
  height: '100%',
  width: '100%'
};

const PANEL_STYLE = RTL => ({
  maxHeight: '100%',
  minHeight: '100%',
  height: '100%',
  minWidth: '25vw',
  maxWidth: '33vw',
  width: '25vw',
  position: 'absolute' as const,
  top: 0,
  right: RTL ? 'auto' : 0,
  left: RTL ? 0 : 'auto',
  zIndex: `${zIndex.OVERLAY_PANEL}`,
});

export interface OverlayPanelProps extends React.ComponentPropsWithRef<'div'> {
  /** The content of the `<OverlayPanel />`, consider using `<OverlayPanelHeader />` to make the header on top of the content. */
  children: React.ReactNode;
  /** If `true`, the component will be visible. */
  visible?: boolean;
  /** To configure the `<Elevation />` component. Refer to the `<Elevation />` document to learn more. Note: Styling and theming of the background are handled by `<Elevation />`. */
  elevationProps?: Partial<ElevationProps>;
  /** Control animation config here. This uses React-spring transitions config. (useSpring hook) See https://react-spring.io for more information. */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  style?: React.CSSProperties;
}

/** `OverlayPanel`, all other props will be provided to the root DOM element. `<Elevation /> is used for handling the background and shadow style.` */
const OverlayPanel = React.forwardRef<HTMLDivElement, OverlayPanelProps>((props: OverlayPanelProps, ref) => getContext(({ RTL, theme: defaultTheme }) => {
  const {
    children,
    elevationProps,
    style,
    visible,
    theme: themeProp,
    ...otherProps
  } = props;
  const {
    style: elevationStyle,
    ...otherElevationProps
  } = elevationProps;

  const resolvedStyle = {
    ...PANEL_STYLE(RTL),
    ...style
  };

  const resolvedTheme = _merge({}, defaultTheme.OVERLAY_PANEL, themeProp);

  return (
    <OverlayPanelContainer
      style={resolvedStyle}
      ref={ref}
      visible={visible}
      {...otherProps}
    >
      <Elevation
        style={{
          ...ELEVATION_STYLE,
          ...elevationStyle
        }}
        {...ELEVATION_CONFIG(visible)}
        {...otherElevationProps}
      >
        <HorizontalDivider theme={{ color: resolvedTheme.divider }} />
        {children}
      </Elevation>
    </OverlayPanelContainer>
  );
}));

OverlayPanel.defaultProps = {
  visible: false,
  elevationProps: {},
  animationProps: {},
  disableAnimation: undefined,
  theme: {},
  style: {}
};

OverlayPanel.displayName = 'OverlayPanel';

export default OverlayPanel;
